'use client'

import React, { useState, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'

export type GuideChapterNavItem = {
  id?: string | null
  number?: string | null
  title?: string | null
}

interface GuideChapterNavProps {
  chapters: GuideChapterNavItem[]
}

export function GuideChapterNav({ chapters }: GuideChapterNavProps) {
  const [activeChapter, setActiveChapter] = useState(0)
  const [isNavOpen, setIsNavOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200

      for (let i = chapters.length - 1; i >= 0; i--) {
        const chapter = document.getElementById(`chapter-${i}`)
        if (chapter && chapter.offsetTop <= scrollPosition) {
          setActiveChapter(i)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [chapters.length])

  const scrollToChapter = (index: number) => {
    const chapter = document.getElementById(`chapter-${index}`)
    if (chapter) {
      const offset = 100
      const elementPosition = chapter.offsetTop - offset
      window.scrollTo({ top: elementPosition, behavior: 'smooth' })
    }
    setIsNavOpen(false)
  }

  if (chapters.length === 0) return null

  return (
    <>
      <aside className="hidden lg:block w-72 shrink-0 roadmap-nav">
        <div className="sticky top-32">
          <nav className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-4">
              Contents
            </h3>
            <ul className="space-y-2">
              {chapters.map((chapter, index) => (
                <li key={chapter.id || index}>
                  <button
                    type="button"
                    onClick={() => scrollToChapter(index)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-3 ${
                      activeChapter === index
                        ? 'bg-brand-gold/10 text-brand-gold font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <span className="text-xs font-mono text-brand-gold/70 w-6">
                      {chapter.number || String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="line-clamp-2">{chapter.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </aside>

      <div className="lg:hidden sticky top-20 z-30 -mx-6 px-6 py-4 bg-background/95 backdrop-blur-sm border-b border-gray-200 roadmap-mobile-nav">
        <button
          type="button"
          onClick={() => setIsNavOpen(!isNavOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-gray-200"
        >
          <span className="text-sm font-medium text-gray-900">
            {chapters[activeChapter]?.number || String(activeChapter + 1).padStart(2, '0')}.{' '}
            {chapters[activeChapter]?.title}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-gray-600 transition-transform ${isNavOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {isNavOpen && (
          <div className="absolute left-6 right-6 mt-2 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden z-30">
            <ul className="py-2">
              {chapters.map((chapter, index) => (
                <li key={chapter.id || index}>
                  <button
                    type="button"
                    onClick={() => scrollToChapter(index)}
                    className={`w-full text-left px-4 py-3 text-sm transition-all flex items-center gap-3 ${
                      activeChapter === index
                        ? 'bg-brand-gold/10 text-brand-gold font-medium'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xs font-mono text-brand-gold/70 w-6">
                      {chapter.number || String(index + 1).padStart(2, '0')}
                    </span>
                    {chapter.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  )
}
